import type { AccountStatusApiResponse } from '@identity/infrastructure/api/types';
import { createAccountStatus } from '@identity/domain/entities';
import type { AccountRequirements } from '@identity/domain/types';
import type { AccountStatus } from '@identity/domain/entities';

/** Turns the `null` the backend may send for an absent field into an absent value. */
const optional = (value?: string | null): string | undefined => value ?? undefined;

/**
 * Converts the account status response into the domain entity.
 *
 * The requirement map and the enforcement mode are copied exactly as they arrived, unknown keys and
 * values included: normalising either here would hide a backend that started sending something this
 * build has never seen, and would let the wizard finish with a requirement still pending.
 *
 * The three optional account fields are copied the same way. A phone that is not E.164 or a date
 * that does not parse reaches the screen as it arrived, because a value corrected here is a contract
 * break nobody ever notices.
 */
export const mapApiStatusToAccountStatus = (response: AccountStatusApiResponse): AccountStatus =>
  createAccountStatus({
    requirements: { ...response.requirements } as AccountRequirements,
    account: {
      firstName: response.account?.firstName ?? '',
      email: response.account?.email ?? '',
      lastName: optional(response.account?.lastName),
      phone: optional(response.account?.phone),
      passwordChangedAt: optional(response.account?.passwordChangedAt),
    },
    enforcementMode: response.enforcementMode,
  });
